import {
  Printer,
  Phone,
  Globe,
  Calendar,
  ShieldCheck,
  CloudRain,
  Gem,
  Wrench,
  Paintbrush,
  Home as HomeIcon,
  Hammer,
  Layers,
  Brush,
  Check,
} from "lucide-react";
import { SITE } from "@/lib/site";

const PROD = "https://swedcro.se";

// Curated real project images from production (public /api/projects/{id}/file)
const IMAGES = {
  fasad: [
    `${PROD}/api/projects/3f577cf7-1f5b-41ea-834b-aaf35433cedc/file`,
    `${PROD}/api/projects/c04d30e7-5ba0-4067-95e2-972aee5e90c5/file`,
    `${PROD}/api/projects/855fc10b-c313-441f-9aad-3d2750e46d65/file`,
    `${PROD}/api/projects/5cfec86a-e6d0-43d0-aef1-3ed9e16eb08d/file`,
    `${PROD}/api/projects/92da8f4b-ab3e-4d1d-86c0-2384d990f329/file`,
  ],
  maleri: [
    `${PROD}/api/projects/7a6864a8-f0a6-47e1-9076-b11930b9f6c5/file`,
    `${PROD}/api/projects/e31fd6b0-fe5f-4d99-b88c-dcb05acd7569/file`,
    `${PROD}/api/projects/7360028c-1d12-48c1-b948-2a6479183795/file`,
    `${PROD}/api/projects/86aa3f1b-1822-4c72-b8c3-9714e333628a/file`,
  ],
  renovering: [
    `${PROD}/api/projects/7d442763-57e2-4553-a06d-f6e2ebf118a4/file`,
    `${PROD}/api/projects/2fed8af6-85cf-4220-9675-eda5b6772129/file`,
    `${PROD}/api/projects/f58d8dde-3457-44a3-b75c-f9e379ac82be/file`,
    `${PROD}/api/projects/9f404a3c-77a9-403e-9387-7b3892d02fef/file`,
  ],
  tapetsering: [
    `${PROD}/api/projects/4b15238c-e53e-4712-bfef-ca50d9319db2/file`,
    `${PROD}/api/projects/0c20c95e-c17a-421e-a310-79cd6f569927/file`,
    `${PROD}/api/projects/8549446e-b475-4421-ae56-0a9bba7d2438/file`,
    `${PROD}/api/projects/d442daa6-c940-4b07-a7eb-fd500e86b616/file`,
  ],
};

export default function Flyer() {
  return (
    <div className="flyer-root">
      <div className="flyer-toolbar no-print">
        <div className="flyer-toolbar-inner">
          <span className="font-display font-black text-lg tracking-tight text-white">
            Swedcro — A5 Flygblad
          </span>
          <button
            data-testid="flyer-print"
            onClick={() => window.print()}
            className="inline-flex items-center gap-2 bg-[#D92525] hover:bg-[#B91C1C] text-white px-5 py-2 font-display font-bold text-xs tracking-[0.15em] uppercase border-2 border-[#D92525] transition-colors"
          >
            <Printer size={14} strokeWidth={2.5} /> Skriv ut / PDF
          </button>
        </div>
      </div>

      <div className="flyer-print-area">
        <FlyerPage />
      </div>

      <style>{`
        @page { size: A5 portrait; margin: 0; }
        html, body, #root, .App { background: #F3F4F6; }
        .flyer-root { min-height: 100vh; padding-bottom: 40px; }
        .flyer-toolbar { position: sticky; top: 0; z-index: 50; background: #0B132B; border-bottom: 2px solid #D92525; }
        .flyer-toolbar-inner { max-width: 1100px; margin: 0 auto; padding: 12px 20px; display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap; }
        .flyer-print-area { display: flex; justify-content: center; padding: 24px 16px; }
        .flyer-page {
          width: 148mm; height: 210mm; background: #ffffff; color: #0B132B;
          box-shadow: 0 20px 60px rgba(0,0,0,0.25); position: relative; overflow: hidden;
          font-family: "Space Grotesk", "Inter", system-ui, sans-serif;
          display: flex; flex-direction: column;
        }
        .flyer-page * { box-sizing: border-box; }
        @media print {
          html, body, #root, .App { background: #ffffff !important; }
          .no-print { display: none !important; }
          .flyer-print-area { padding: 0 !important; }
          .flyer-page { width: 148mm !important; height: 210mm !important; box-shadow: none !important; page-break-after: always; }
        }
      `}</style>
    </div>
  );
}

/* ---------------- Flyer Page ---------------- */
const FlyerPage = () => {
  const heroImg = IMAGES.fasad[0] || SITE.hero;
  const pairs = [
    [IMAGES.fasad[1], IMAGES.fasad[3]],
    [IMAGES.tapetsering[3], IMAGES.tapetsering[1]],
    [IMAGES.renovering[0], IMAGES.renovering[2]],
  ];
  const bottomProjects = [IMAGES.tapetsering[2], IMAGES.maleri[1], IMAGES.maleri[3]];

  return (
    <div className="flyer-page" data-testid="flyer-page">
      {/* ============ HEADER ============ */}
      <div style={{ padding: "4mm 5mm 1.5mm 5mm" }}>
        <div style={{ display: "grid", gridTemplateColumns: "54% 46%", gap: "3mm", alignItems: "start" }}>
          {/* Left */}
          <div>
            <div style={{ fontFamily: "Space Grotesk, sans-serif", fontWeight: 900, fontSize: "18pt", lineHeight: 0.95, letterSpacing: "-0.035em", color: "#0B132B" }}>
              SWED<span style={{ color: "#D92525" }}>CRO</span>
            </div>
            <div style={{
              display: "inline-block", marginTop: "0.8mm",
              borderTop: "1.2pt solid #D92525", borderBottom: "1.2pt solid #D92525",
              padding: "0.4mm 1.5mm",
              fontSize: "6pt", letterSpacing: "0.28em", fontWeight: 800, color: "#0B132B",
            }}>
              MÅLERI &nbsp;&amp;&nbsp; FASAD
            </div>
            <div style={{ marginTop: "1.5mm" }}>
              <div style={{ fontSize: "8pt", fontWeight: 900, color: "#0B132B", letterSpacing: "0.05em" }}>VI UTFÖR:</div>
              <ul style={{ margin: "0.6mm 0 0 0", padding: 0, listStyle: "none", display: "grid", gap: "0.1mm" }}>
                {["Måleri", "Fasadarbete", "Renovering", "Tapetsering", "Akrylfasad & Sockelputs"].map((s) => (
                  <li key={s} style={{ display: "flex", alignItems: "center", gap: "1.2mm", fontSize: "7pt", fontWeight: 700, color: "#0B132B" }}>
                    <Check size={7} color="#D92525" strokeWidth={3.5} />
                    {s}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Right hero */}
          <div style={{ position: "relative", height: "32mm", overflow: "hidden", border: "1.2pt solid #0B132B" }}>
            <img src={heroImg} alt="Referens" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} referrerPolicy="no-referrer" />
            <div style={{
              position: "absolute", bottom: "1mm", right: "1mm",
              background: "#D92525", color: "#ffffff", padding: "1mm 1.5mm",
              display: "flex", alignItems: "center", gap: "1.2mm",
              boxShadow: "0 2px 6px rgba(0,0,0,0.25)",
            }}>
              <ShieldCheck size={9} color="#ffffff" strokeWidth={2.5} />
              <div>
                <div style={{ fontSize: "6.5pt", fontWeight: 900, lineHeight: 1 }}>KVALITETSGARANTI</div>
                <div style={{ fontSize: "4.5pt", letterSpacing: "0.15em", fontWeight: 700, opacity: 0.95, marginTop: "0.2mm" }}>DIN TRYGGHET · VÅRT ANSVAR</div>
              </div>
            </div>
          </div>
        </div>

        {/* Big headline */}
        <div style={{ marginTop: "1.5mm" }}>
          <h1 style={{ margin: 0, fontSize: "21pt", fontWeight: 900, lineHeight: 0.9, letterSpacing: "-0.035em", color: "#0B132B" }}>
            FRÅN SLITET <span style={{ color: "#D92525" }}>TILL SOM NYTT!</span>
          </h1>
          <div style={{
            marginTop: "0.8mm", background: "#0B132B", color: "#ffffff",
            padding: "0.8mm 2mm", display: "inline-block",
            fontSize: "6.5pt", fontWeight: 800, letterSpacing: "0.22em",
          }}>
            PROFESSIONELLT · KVALITET · PÅLITLIGT
          </div>
        </div>
      </div>

      {/* ============ MOSAIK SECTION ============ */}
      <div style={{ margin: "0 5mm", border: "1.5pt solid #0B132B", padding: "2.5mm", background: "#FAFAFA" }}>
        <div style={{ display: "grid", gridTemplateColumns: "24mm 1fr", gap: "2.5mm", alignItems: "center" }}>
          <div>
            <div style={{ fontFamily: "Space Grotesk, sans-serif", fontWeight: 900, fontSize: "14pt", color: "#D92525", letterSpacing: "-0.02em", lineHeight: 0.9 }}>
              KULIR
            </div>
            <div style={{ fontSize: "7pt", fontWeight: 900, color: "#0B132B", marginTop: "0.6mm", lineHeight: 1.05 }}>
              MOSAIK<br/>SOCKELPUTS
            </div>
            <div style={{ fontSize: "5pt", letterSpacing: "0.16em", fontWeight: 800, color: "#D92525", marginTop: "0.8mm" }}>
              HÅLLBAR · VACKER · TIDLÖS
            </div>
            <div style={{ fontSize: "5.5pt", color: "#4B5563", marginTop: "0.8mm", lineHeight: 1.3 }}>
              Slitstark och väderbeständig sockelputs med äkta stenkänsla.
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: "1.2mm" }}>
            {[
              { src: "/mosaik-samples/sample-m2-320.jpg", label: "M2 320" },
              { src: "/mosaik-samples/sample-m4-136.jpg", label: "M4 136" },
              { src: "/mosaik-samples/sample-m9-888.jpg", label: "M9 888" },
              { src: "/mosaik-samples/sample-m9-922.jpg", label: "M9 922" },
              { src: "/mosaik-samples/sample-m9-924.jpg", label: "M9 924" },
            ].map((s) => (
              <div key={s.label} style={{ textAlign: "center" }}>
                <div style={{
                  width: "100%", aspectRatio: "1/1",
                  backgroundImage: `url("${s.src}")`, backgroundSize: "cover", backgroundPosition: "center",
                  border: "1pt solid #0B132B",
                }} />
                <div style={{ fontSize: "6pt", fontWeight: 900, color: "#0B132B", marginTop: "0.5mm", lineHeight: 1 }}>{s.label}</div>
                <div style={{ fontSize: "4.5pt", color: "#6B7280", fontWeight: 600, marginTop: "0.2mm" }}>1.5 mm</div>
              </div>
            ))}
          </div>
        </div>

        <div style={{ marginTop: "2mm", display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "1.5mm" }}>
          {[
            { Icon: ShieldCheck, label: "HÖG SLITSTYRKA" },
            { Icon: CloudRain, label: "VÄDERBESTÄNDIG" },
            { Icon: Gem, label: "ÄKTA STENKÄNSLA" },
            { Icon: Wrench, label: "ENKEL APPLICERING" },
          ].map(({ Icon, label }) => (
            <div key={label} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "0.7mm" }}>
              <div style={{ width: "7.5mm", height: "7.5mm", borderRadius: "50%", border: "1.2pt solid #0B132B", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Icon size={12} color="#D92525" strokeWidth={2.2} />
              </div>
              <div style={{ fontSize: "5pt", fontWeight: 900, color: "#0B132B", letterSpacing: "0.08em" }}>{label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ============ SERVICE ICONS ROW ============ */}
      <div style={{ padding: "2.5mm 5mm 1.5mm 5mm", display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: "1.5mm" }}>
        {[
          { Icon: Paintbrush, title: "MÅLERI", sub: "Invändig & utvändig målning" },
          { Icon: HomeIcon, title: "FASAD", sub: "Puts, målning & fasadarbete" },
          { Icon: Hammer, title: "RENOVERING", sub: "Ombyggnad & förbättring" },
          { Icon: Layers, title: "TAPETSERING", sub: "Tapetsering & väggförnyelse" },
          { Icon: Brush, title: "MOSAIK / SOCKELPUTS", sub: "Hållbar & dekorativ sockelputs" },
        ].map(({ Icon, title, sub }) => (
          <div key={title} style={{ textAlign: "center" }}>
            <div style={{ width: "10mm", height: "10mm", margin: "0 auto", borderRadius: "50%", border: "1.2pt solid #0B132B", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Icon size={15} color="#D92525" strokeWidth={2.2} />
            </div>
            <div style={{ fontSize: "6pt", fontWeight: 900, color: "#0B132B", marginTop: "0.8mm", letterSpacing: "0.03em", lineHeight: 1 }}>{title}</div>
            <div style={{ fontSize: "4.8pt", color: "#4B5563", marginTop: "0.4mm", lineHeight: 1.2 }}>{sub}</div>
          </div>
        ))}
      </div>

      {/* ============ FÖRE / EFTER ============ */}
      <div style={{ padding: "1mm 5mm 0 5mm", display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "1.5mm" }}>
        {pairs.map((pair, i) => (
          <div key={i} style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.6mm" }}>
            {pair.map((img, j) => (
              <div key={j} style={{ position: "relative", aspectRatio: "1/1.15", overflow: "hidden", border: "1pt solid #0B132B" }}>
                <img src={img} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} referrerPolicy="no-referrer" />
                <div style={{
                  position: "absolute", top: 0, left: 0,
                  background: j === 0 ? "#D92525" : "#0B132B", color: "#ffffff",
                  padding: "0.5mm 1.2mm",
                  fontSize: "5.5pt", fontWeight: 900, letterSpacing: "0.1em",
                }}>
                  {j === 0 ? "FÖRE" : "EFTER"}
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>

      {/* ============ CONTACT BAR ============ */}
      <div style={{
        marginTop: "2mm", background: "#0B132B", color: "#ffffff",
        padding: "2.5mm 5mm", display: "grid", gridTemplateColumns: "1fr 1fr 1fr auto", gap: "2.5mm", alignItems: "center",
      }}>
        <ContactCell icon={<Globe size={11} strokeWidth={2.5} />} label="BESÖK OSS PÅ" value="WWW.SWEDCRO.SE" />
        <ContactCell icon={<Phone size={11} strokeWidth={2.5} />} label="RING OSS IDAG" value={SITE.phone} />
        <ContactCell icon={<Calendar size={11} strokeWidth={2.5} />} label="BOKA OFFERT IDAG!" value="SNABB & KOSTNADSFRI OFFERT" small />
        <div style={{ background: "#ffffff", padding: "1.2mm", lineHeight: 0 }}>
          <img
            src="/qr-swedcro.png"
            alt="QR kod - swedcro.se"
            style={{ width: "20mm", height: "20mm", display: "block", objectFit: "contain" }}
          />
          <div style={{ textAlign: "center", fontSize: "4.5pt", fontWeight: 900, color: "#0B132B", letterSpacing: "0.1em", marginTop: "0.3mm" }}>
            BESÖK VÅR HEMSIDA
          </div>
        </div>
      </div>

      {/* ============ BOTTOM: PROJECTS + ABOUT ============ */}
      <div style={{ padding: "1.5mm 5mm 1mm 5mm", display: "grid", gridTemplateColumns: "40% 60%", gap: "3mm" }}>
        <div>
          <div style={{ fontSize: "6.5pt", fontWeight: 900, color: "#0B132B", letterSpacing: "0.05em" }}>NÅGRA AV VÅRA PROJEKT</div>
          <div style={{ marginTop: "0.8mm", display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "0.6mm" }}>
            {bottomProjects.map((img, i) => (
              <div key={i} style={{ aspectRatio: "1/1.1", overflow: "hidden", border: "1pt solid #0B132B" }}>
                <img src={img} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} referrerPolicy="no-referrer" />
              </div>
            ))}
          </div>
        </div>

        <div>
          <div style={{ fontSize: "6.5pt", fontWeight: 900, color: "#0B132B", letterSpacing: "0.02em" }}>
            OM SWED<span style={{ color: "#D92525" }}>CRO</span> MÅLERI &amp; FASAD
          </div>
          <p style={{ margin: "0.4mm 0 0 0", fontSize: "5.3pt", lineHeight: 1.3, color: "#0B132B" }}>
            Vi är ett engagerat och pålitligt företag som erbjuder professionella måleri- och fasadtjänster för både privatpersoner och företag i Eskilstuna med närområde. Vårt mål är att leverera hög kvalitet och ett resultat som överträffar dina förväntningar.
          </p>
          <ul style={{ margin: "0.8mm 0 0 0", padding: 0, listStyle: "none", display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.2mm 2mm" }}>
            {["Kostnadsfri offert", "Pålitliga & punktliga", "Hög kvalitet & noggrannhet", "Nöjda kunder"].map((b) => (
              <li key={b} style={{ display: "flex", alignItems: "center", gap: "1mm", fontSize: "5.3pt", fontWeight: 700, color: "#0B132B" }}>
                <Check size={6} color="#D92525" strokeWidth={3.5} />
                {b}
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* ============ FOOTER RED BAR ============ */}
      <div style={{ background: "#D92525", color: "#ffffff", padding: "1.5mm 5mm", textAlign: "center", fontSize: "8pt", fontWeight: 900, letterSpacing: "0.15em", marginTop: "auto" }}>
        KVALITET SOM SYNS — RESULTAT SOM BESTÅR!
      </div>
    </div>
  );
};

const ContactCell = ({ icon, label, value, small }) => (
  <div style={{ display: "flex", alignItems: "center", gap: "1.5mm" }}>
    <div style={{ background: "#D92525", padding: "1.2mm", display: "flex", flexShrink: 0 }}>{icon}</div>
    <div>
      <div style={{ fontSize: "5pt", letterSpacing: "0.2em", fontWeight: 700, opacity: 0.75 }}>{label}</div>
      <div style={{ fontSize: small ? "6pt" : "8pt", fontWeight: 900, letterSpacing: "-0.01em", marginTop: "0.2mm", lineHeight: 1 }}>{value}</div>
    </div>
  </div>
);
